/**
 * Page shell for the Resale Pricing section: one "Resale Pricing" page at
 * /resalePricing whose subpages render as tabs (Overview, Price Rules,
 * Overrides, Condition Discounts, Rounding Rules) instead of separate
 * sidebar entries. Each tab is still its own route, so deep links and the
 * resources' create/edit/history subroutes keep working.
 */
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

export type ResalePricingTab =
  | 'overview'
  | 'pricingRules'
  | 'itemAdjustedPrices'
  | 'conditionDiscounts'
  | 'roundingRules';

const TABS: { value: ResalePricingTab, label: string, path: string }[] = [
  { value: 'overview', label: 'Overview', path: '/resalePricing' },
  { value: 'pricingRules', label: 'Price Rules', path: '/resalePricing/pricingRules' },
  { value: 'itemAdjustedPrices', label: 'Overrides', path: '/resalePricing/itemAdjustedPrices' },
  { value: 'conditionDiscounts', label: 'Condition Discounts', path: '/resalePricing/conditionDiscounts' },
  { value: 'roundingRules', label: 'Rounding Rules', path: '/resalePricing/roundingRules' },
];

const TAB_DESCRIPTIONS: Record<ResalePricingTab, string> = {
  overview: 'Understand how prices are calculated, then jump to the right configuration page.',
  pricingRules: 'Ordered rules that derive a resale price from a source price. The first matching rule wins.',
  itemAdjustedPrices: 'SKU-level price overrides currently in effect, uploaded as markdowns.',
  conditionDiscounts: 'Discounts applied on top of the resale price based on item condition.',
  roundingRules: 'How calculated prices round to their final storefront value.',
};

interface ResalePricingTabShellProps {
  tab: ResalePricingTab;
  children: React.ReactNode;
}

const ResalePricingTabShell = ({ tab, children }: ResalePricingTabShellProps) => (
  <Box>
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, fontSize: 22 }}>
        Resale Pricing
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
        {TAB_DESCRIPTIONS[tab]}
      </Typography>
    </Box>
    <Tabs
      value={tab}
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        minHeight: 40,
        '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 500 },
      }}
    >
      {TABS.map(({ value, label, path }) => (
        <Tab key={value} value={value} label={label} component={Link} to={path} />
      ))}
    </Tabs>
    <Box sx={{ pt: 1 }}>{children}</Box>
  </Box>
);

export default ResalePricingTabShell;
